import React, { useEffect, useMemo, useState } from "react";
import { Alert, Container, Spinner, Table } from "react-bootstrap";
import { useParams } from "react-router-dom";
import axios from "../utils/api";

const API_URL = process.env.REACT_APP_API_URL_BASE || "http://localhost:6020/";

function InvoiceDetail() {
  const { id } = useParams();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedRawLines, setExpandedRawLines] = useState(() => new Set());
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("asc"); // "asc" | "desc"

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API_URL}api/invoice/${id}`);
        if (!res.data?.success) throw new Error(res.data?.message || "Failed");
        setInvoice(res.data.invoice);
      } catch (err) {
        setError(err.response?.data?.message || err.message || "Error loading invoice");
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  /* ── sorting helpers ── */
  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const SortHeader = ({ col, children }) => {
    const arrow = sortKey === col ? (sortDir === "asc" ? " ▲" : " ▼") : "";
    return (
      <th
        role="button"
        style={{ cursor: "pointer", userSelect: "none", whiteSpace: "nowrap" }}
        onClick={() => handleSort(col)}
      >
        {children}{arrow}
      </th>
    );
  };

  const sortItems = (items, accessor) => {
    if (!sortKey) return items;
    const sorted = [...items].sort((a, b) => {
      const va = accessor(a, sortKey);
      const vb = accessor(b, sortKey);
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      if (typeof va === "string") return va.localeCompare(vb);
      return va - vb;
    });
    return sortDir === "desc" ? sorted.reverse() : sorted;
  };

  const toggleRawLines = (key) => {
    setExpandedRawLines((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const RawLinesToggle = ({ itemKey, item, colSpan }) => {
    const lines = item.raw_lines;
    if (!lines || !lines.length) return null;
    return expandedRawLines.has(itemKey) ? (
      <tr>
        <td colSpan={colSpan} style={{ padding: "4px 12px", background: "#f8f9fa" }}>
          <pre style={{ margin: 0, fontSize: "0.75em", whiteSpace: "pre-wrap", color: "#555" }}>{lines.join("\n")}</pre>
        </td>
      </tr>
    ) : null;
  };

  const inv = invoice;
  const lineItems = (inv && inv.items) || [];

  const isPitco =
    (inv?.vendor_name || "").toLowerCase().includes("pitco") ||
    lineItems.some((it) => "out_of_stock" in it && "product_number" in it && "crv_per_unit" in it);
  const isCoke =
    !isPitco && lineItems.some((it) => it.sku && !it.product_number);

  // --- Pitco: enrich + sort ---
  const pitcoItems = useMemo(() => {
    if (!isPitco) return [];
    const enriched = lineItems.map((item, idx) => {
      const costPerUnit = typeof item.price_per_unit === "number" ? item.price_per_unit : null;
      const posPrice =
        typeof item.posPrice === "number"
          ? item.posPrice
          : typeof item.posCents === "number"
          ? item.posCents / 100
          : null;
      const margin =
        costPerUnit !== null && posPrice !== null && posPrice > 0
          ? ((posPrice - costPerUnit) / posPrice) * 100
          : null;
      return { ...item, _idx: idx, _costPerUnit: costPerUnit, _posPrice: posPrice, _margin: margin };
    });
    const accessor = (it, key) => {
      const map = {
        pack_upc: it.pack_upc || "",
        product_number: it.product_number || "",
        description: (it.description || "").toLowerCase(),
        qty: parseFloat(it.qty) || 0,
        uom: it.uom || "",
        ppc: parseFloat(it.ppc) || 0,
        costPerUnit: it._costPerUnit,
        posPrice: it._posPrice,
        margin: it._margin,
        retail: typeof it.retail === "number" ? it.retail : null,
        gp_pct: typeof it.gp_pct === "number" ? it.gp_pct : null,
        allow: typeof it.allow === "number" ? it.allow : null,
        crv_total: typeof it.crv_total === "number" ? it.crv_total : null,
        total: typeof it.total === "number" ? it.total : null,
        oos: it.out_of_stock ? 1 : 0,
      };
      return map[key] ?? null;
    };
    return sortItems(enriched, accessor);
  }, [isPitco, lineItems, sortKey, sortDir]);

  const renderPitco = () => (
    <div className="table-responsive">
      <Table striped bordered hover size="sm">
        <thead className="table-light">
          <tr>
            <th>#</th>
            <SortHeader col="pack_upc">UPC</SortHeader>
            <SortHeader col="product_number">Item#</SortHeader>
            <SortHeader col="description">Description</SortHeader>
            <SortHeader col="qty">Qty</SortHeader>
            <SortHeader col="uom">U/M</SortHeader>
            <SortHeader col="ppc">PPC</SortHeader>
            <SortHeader col="costPerUnit">Cost/Unit</SortHeader>
            <SortHeader col="posPrice">POS Price</SortHeader>
            <SortHeader col="margin">Margin</SortHeader>
            <SortHeader col="retail">Retail</SortHeader>
            <SortHeader col="gp_pct">GP%</SortHeader>
            <SortHeader col="allow">Allow</SortHeader>
            <SortHeader col="crv_total">CRV</SortHeader>
            <SortHeader col="total">Total</SortHeader>
            <SortHeader col="oos">OOS</SortHeader>
          </tr>
        </thead>
        <tbody>
          {pitcoItems.map((item, idx) => {
            const isOOS = item.out_of_stock;
            const rawKey = `pitco-${item._idx}`;
            return (
              <React.Fragment key={rawKey}>
                <tr style={isOOS ? { opacity: 0.5 } : {}}>
                  <td>
                    {item.raw_lines?.length ? (
                      <span role="button" style={{ cursor: "pointer", userSelect: "none" }} onClick={() => toggleRawLines(rawKey)}>
                        {expandedRawLines.has(rawKey) ? "−" : "+"}
                      </span>
                    ) : null}{" "}{idx + 1}
                  </td>
                  <td><code style={{ fontSize: "0.8em" }}>{item.pack_upc || "—"}</code></td>
                  <td>{item.product_number || "—"}</td>
                  <td>{item.description || "—"}</td>
                  <td>{item.qty ?? "—"}</td>
                  <td>{item.uom || "—"}</td>
                  <td>{item.ppc ?? "—"}</td>
                  <td>{item._costPerUnit !== null ? `$${item._costPerUnit.toFixed(2)}` : "—"}</td>
                  <td>{item._posPrice !== null ? `$${item._posPrice.toFixed(2)}` : "—"}</td>
                  <td style={item._margin !== null ? { color: item._margin >= 25 ? "green" : item._margin >= 10 ? "orange" : "red", fontWeight: 600 } : {}}>
                    {item._margin !== null ? `${item._margin.toFixed(1)}%` : "—"}
                  </td>
                  <td>{typeof item.retail === "number" ? `$${item.retail.toFixed(2)}` : "—"}</td>
                  <td>{typeof item.gp_pct === "number" ? `${item.gp_pct.toFixed(1)}%` : "—"}</td>
                  <td>{typeof item.allow === "number" && item.allow !== 0 ? `$${item.allow.toFixed(2)}` : "—"}</td>
                  <td>{typeof item.crv_total === "number" && item.crv_total > 0 ? `$${item.crv_total.toFixed(2)}` : "—"}</td>
                  <td>{typeof item.total === "number" ? `$${item.total.toFixed(2)}` : "—"}</td>
                  <td>{isOOS ? <span title="Out of Stock" style={{ color: "red", fontWeight: 700 }}>⊘</span> : ""}</td>
                </tr>
                <RawLinesToggle itemKey={rawKey} item={item} colSpan={16} />
              </React.Fragment>
            );
          })}
        </tbody>
      </Table>
    </div>
  );

  // --- Coke: enrich + sort ---
  const cokeItems = useMemo(() => {
    if (!isCoke) return [];
    const enriched = lineItems.map((item, idx) => {
      const costPerUnit =
        typeof item.price_per_unit === "number"
          ? item.price_per_unit
          : (() => {
              const total = typeof item.total === "number" ? item.total : null;
              const ppc = parseFloat(item.ppc);
              const qty = parseFloat(item.qty);
              const d = ppc * qty;
              return total !== null && d > 0 ? +(total / d).toFixed(2) : null;
            })();
      const posPrice =
        typeof item.posPrice === "number"
          ? item.posPrice
          : typeof item.posCents === "number"
          ? item.posCents / 100
          : null;
      const margin =
        costPerUnit !== null && posPrice !== null && posPrice > 0
          ? ((posPrice - costPerUnit) / posPrice) * 100
          : null;
      return { ...item, _idx: idx, _costPerUnit: costPerUnit, _posPrice: posPrice, _margin: margin };
    });
    const accessor = (it, key) => {
      const map = {
        sku: it.sku || "",
        description: (it.description || "").toLowerCase(),
        pack_upc: it.pack_upc || "",
        qty: parseFloat(it.qty) || 0,
        ppc: parseFloat(it.ppc) || 0,
        costPerUnit: it._costPerUnit,
        posPrice: it._posPrice,
        margin: it._margin,
        discount: typeof it.discount === "number" ? it.discount : null,
        crv: typeof it.crv === "number" ? it.crv : null,
        total: typeof it.total === "number" ? it.total : null,
      };
      return map[key] ?? null;
    };
    return sortItems(enriched, accessor);
  }, [isCoke, lineItems, sortKey, sortDir]);

  const renderCoke = () => (
    <div className="table-responsive">
      <Table striped bordered hover size="sm">
        <thead className="table-light">
          <tr>
            <th>#</th>
            <SortHeader col="sku">SKU</SortHeader>
            <SortHeader col="description">Description</SortHeader>
            <SortHeader col="pack_upc">UPC</SortHeader>
            <SortHeader col="qty">Qty</SortHeader>
            <SortHeader col="ppc">PPC</SortHeader>
            <SortHeader col="costPerUnit">Cost/Unit</SortHeader>
            <SortHeader col="posPrice">POS Price</SortHeader>
            <SortHeader col="margin">Margin</SortHeader>
            <SortHeader col="discount">Discount</SortHeader>
            <SortHeader col="crv">CRV</SortHeader>
            <SortHeader col="total">Total</SortHeader>
          </tr>
        </thead>
        <tbody>
          {cokeItems.map((item, idx) => {
            const rawKey = `coke-${item._idx}`;
            return (
              <React.Fragment key={rawKey}>
                <tr>
                  <td>
                    {item.raw_lines?.length ? (
                      <span role="button" style={{ cursor: "pointer", userSelect: "none" }} onClick={() => toggleRawLines(rawKey)}>
                        {expandedRawLines.has(rawKey) ? "−" : "+"}
                      </span>
                    ) : null}{" "}{idx + 1}
                  </td>
                  <td><code>{item.sku || "—"}</code></td>
                  <td>{item.description || "—"}</td>
                  <td>{item.pack_upc || <span className="text-muted" style={{ fontSize: "0.8em" }}>no map</span>}</td>
                  <td>{item.qty ?? "—"}</td>
                  <td>{item.ppc ?? "—"}</td>
                  <td>{item._costPerUnit !== null ? `$${item._costPerUnit.toFixed(2)}` : "—"}</td>
                  <td>{item._posPrice !== null ? `$${item._posPrice.toFixed(2)}` : "—"}</td>
                  <td style={item._margin !== null ? { color: item._margin >= 25 ? "green" : item._margin >= 10 ? "orange" : "red", fontWeight: 600 } : {}}>
                    {item._margin !== null ? `${item._margin.toFixed(1)}%` : "—"}
                  </td>
                  <td>{typeof item.discount === "number" ? `$${item.discount.toFixed(2)}` : "—"}</td>
                  <td>{typeof item.crv === "number" ? `$${item.crv.toFixed(2)}` : "—"}</td>
                  <td>{typeof item.total === "number" ? `$${item.total.toFixed(2)}` : "—"}</td>
                </tr>
                <RawLinesToggle itemKey={rawKey} item={item} colSpan={12} />
              </React.Fragment>
            );
          })}
        </tbody>
      </Table>
    </div>
  );

  // --- Southern / default: enrich + sort ---
  const defaultItems = useMemo(() => {
    if (isPitco || isCoke) return [];
    const enriched = lineItems.map((item, idx) => {
      const ppu = (() => {
        const total = typeof item.total === "number" ? item.total : null;
        const ppc = parseFloat(item.ppc);
        const qty = parseFloat(item.qty);
        const divisor = ppc * qty;
        if (total !== null && divisor > 0) return total / divisor;
        return null;
      })();
      const posPrice =
        typeof item.posPrice === "number"
          ? item.posPrice
          : typeof item.posCents === "number"
          ? item.posCents / 100
          : null;
      const margin =
        ppu !== null && posPrice !== null && posPrice > 0
          ? ((posPrice - ppu) / posPrice) * 100
          : null;
      return { ...item, _idx: idx, _ppu: ppu, _posPrice: posPrice, _margin: margin };
    });
    const accessor = (it, key) => {
      const map = {
        description: (it.description || "").toLowerCase(),
        case_upc: it.case_upc || "",
        pack_upc: it.pack_upc || "",
        ppu: it._ppu,
        posPrice: it._posPrice,
        margin: it._margin,
        qty: parseFloat(it.qty) || 0,
        ppc: parseFloat(it.ppc) || 0,
        disc: parseFloat(it.disc) || 0,
        adj: parseFloat(it.adj) || 0,
        price: typeof it.price === "number" ? it.price : null,
        total: typeof it.total === "number" ? it.total : null,
      };
      return map[key] ?? null;
    };
    return sortItems(enriched, accessor);
  }, [isPitco, isCoke, lineItems, sortKey, sortDir]);

  const renderDefault = () => (
    <div className="table-responsive">
      <Table striped bordered hover size="sm">
        <thead className="table-light">
          <tr>
            <th>#</th>
            <SortHeader col="description">Description</SortHeader>
            <SortHeader col="case_upc">Case UPC</SortHeader>
            <SortHeader col="pack_upc">Pack UPC</SortHeader>
            <SortHeader col="ppu">Price/Unit</SortHeader>
            <SortHeader col="posPrice">POS Price</SortHeader>
            <SortHeader col="margin">%</SortHeader>
            <SortHeader col="qty">Qty</SortHeader>
            <SortHeader col="ppc">PPC</SortHeader>
            <SortHeader col="disc">Disc</SortHeader>
            <SortHeader col="adj">Adj</SortHeader>
            <SortHeader col="price">Price</SortHeader>
            <SortHeader col="total">Total</SortHeader>
          </tr>
        </thead>
        <tbody>
          {defaultItems.map((item, idx) => {
            const rawKey = `item-${item._idx}`;
            return (
              <React.Fragment key={rawKey}>
                <tr>
                  <td>
                    {item.raw_lines?.length ? (
                      <span role="button" style={{ cursor: "pointer", userSelect: "none" }} onClick={() => toggleRawLines(rawKey)}>
                        {expandedRawLines.has(rawKey) ? "−" : "+"}
                      </span>
                    ) : null}{" "}{idx + 1}
                  </td>
                  <td>{item.description || "—"}</td>
                  <td>{item.case_upc || "—"}</td>
                  <td>{item.pack_upc || "—"}</td>
                  <td>{item._ppu !== null ? `$${item._ppu.toFixed(2)}` : "—"}</td>
                  <td>{item._posPrice !== null ? `$${item._posPrice.toFixed(2)}` : "—"}</td>
                  <td>{item._margin !== null ? `${item._margin.toFixed(1)}%` : "—"}</td>
                  <td>{item.qty ?? "—"}</td>
                  <td>{item.ppc ?? "—"}</td>
                  <td>{item.disc ?? "—"}</td>
                  <td>{item.adj ?? "—"}</td>
                  <td>{typeof item.price === "number" ? `$${item.price.toFixed(2)}` : "—"}</td>
                  <td>{typeof item.total === "number" ? `$${item.total.toFixed(2)}` : "—"}</td>
                </tr>
                <RawLinesToggle itemKey={rawKey} item={item} colSpan={13} />
              </React.Fragment>
            );
          })}
        </tbody>
      </Table>
    </div>
  );

  if (loading) {
    return (
      <Container className="mt-4 text-center py-5">
        <Spinner animation="border" />
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="mt-4">
        <Alert variant="danger">{error}</Alert>
      </Container>
    );
  }

  if (!invoice) return null;

  return (
    <Container className="mt-4 mb-5">
      <div className="d-flex align-items-center justify-content-between mb-3">
        <h3 className="mb-0">
          Invoice #{inv.invoice_number || "—"}{" "}
          <small className="text-muted">— {inv.vendor_name || "Unknown vendor"}</small>
        </h3>
        {inv.pdfPath && (
          <a
            href={`${API_URL}api/invoice/${inv._id}/pdf`}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-outline-secondary btn-sm"
          >
            📄 View Original PDF
          </a>
        )}
      </div>

      <div className="mb-3 text-muted" style={{ fontSize: "0.9em" }}>
        Date: {inv.invoice_date || "—"} &nbsp;|&nbsp;
        Items: {inv.totalItems ?? lineItems.length} &nbsp;|&nbsp;
        Total: {typeof inv.totalAmount === "number" ? `$${inv.totalAmount.toFixed(2)}` : "—"} &nbsp;|&nbsp;
        File: {inv.fileName || "—"}
      </div>

      {lineItems.length === 0 ? (
        <Alert variant="info">No line items for this invoice.</Alert>
      ) : isPitco ? (
        renderPitco()
      ) : isCoke ? (
        renderCoke()
      ) : (
        renderDefault()
      )}
    </Container>
  );
}

export default InvoiceDetail;
